import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCcw, ArrowDown } from "lucide-react";

/**
 * Custom hook che sottoscrive un callback all'evento globale "app:refresh".
 * Ogni pagina lo usa per definire cosa ricaricare quando l'utente tira in giù.
 */
export function useRefreshOnPull(reload) {
  useEffect(() => {
    if (!reload) return;
    const handler = () => reload();
    window.addEventListener("app:refresh", handler);
    return () => window.removeEventListener("app:refresh", handler);
  }, [reload]);
}

/**
 * Dispatcha l'evento di refresh (usato dal PullToRefresh in Layout).
 * Restituisce una promise che si risolve dopo un piccolo delay minimo, così
 * l'utente vede l'animazione anche se il caricamento è istantaneo.
 */
export async function triggerAppRefresh() {
  window.dispatchEvent(new CustomEvent("app:refresh"));
  await new Promise((r) => setTimeout(r, 400));
}

const THRESHOLD = 70;   // px di trascinamento richiesti per attivare
const RESISTANCE = 0.5; // fattore di resistenza (più basso = più duro da tirare)
const MAX_PULL = 110;

/**
 * PullToRefresh: gesture "tira giù per aggiornare" universale.
 * - Attivo solo quando lo scroll è all'inizio (scrollTop <= 0)
 * - Supporta touch (mobile) e mouse-drag (desktop)
 * - Al rilascio, se oltre soglia, chiama onRefresh(); altrimenti torna indietro
 */
export default function PullToRefresh({ onRefresh, children, disabled }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const active = useRef(false);
  const containerRef = useRef(null);

  const canStart = () => {
    if (disabled || refreshing) return false;
    // Solo se in cima allo scroll
    const scroller = document.scrollingElement || document.documentElement;
    return (scroller.scrollTop || window.scrollY || 0) <= 0;
  };

  const begin = (clientY) => {
    if (!canStart()) return;
    startY.current = clientY;
    active.current = true;
  };

  const move = (clientY, e) => {
    if (!active.current || startY.current == null) return;
    const delta = clientY - startY.current;
    if (delta <= 0) { setPull(0); return; }
    // Applica resistenza e cap
    const amount = Math.min(delta * RESISTANCE, MAX_PULL);
    setPull(amount);
    // Blocca lo scroll nativo durante la trazione
    if (e && e.cancelable) e.preventDefault();
  };

  const end = useCallback(async () => {
    if (!active.current) return;
    active.current = false;
    startY.current = null;
    if (pull >= THRESHOLD) {
      setRefreshing(true);
      setPull(THRESHOLD);
      try { await onRefresh?.(); } finally {
        setRefreshing(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
  }, [pull, onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => begin(e.touches[0].clientY);
    const onTouchMove = (e) => move(e.touches[0].clientY, e);
    const onTouchEnd = () => end();

    // Non passivo per poter chiamare preventDefault quando in trazione
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [end]);

  const progress = Math.min(1, pull / THRESHOLD);
  const ready = pull >= THRESHOLD;

  return (
    <div ref={containerRef} className="relative">
      {/* Indicatore in alto */}
      <div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 z-30 flex items-center justify-center transition-opacity"
        style={{
          top: -30 + pull * 0.6,
          opacity: pull > 4 ? 1 : 0,
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div
          className={`h-11 w-11 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center transition-transform ${refreshing ? "" : ready ? "scale-110" : "scale-100"}`}
          style={{ transform: `rotate(${progress * 180}deg)` }}
        >
          {refreshing ? (
            <RefreshCcw className="h-5 w-5 text-amber-500 animate-spin" />
          ) : ready ? (
            <RefreshCcw className="h-5 w-5 text-amber-500" />
          ) : (
            <ArrowDown className="h-5 w-5 text-slate-400" />
          )}
        </div>
      </div>

      <div
        style={{
          transform: `translateY(${refreshing ? THRESHOLD * 0.6 : pull}px)`,
          transition: active.current ? "none" : "transform 240ms cubic-bezier(0.2, 0.9, 0.3, 1.2)",
        }}
      >
        {children}
      </div>
    </div>
  );
}
